import React from 'react';
import { Clock, Check, Lock, Phone, Monitor } from 'lucide-react';
import { useI18n } from '@/i18n';

interface ProcessFlowProps {
  audience: 'tutor' | 'parent';
}

// --- Parent SVG Illustrations ---
const ParentStep1 = ({
  caseLabel,
  subjectLabel,
  locationLabel,
  submitLabel,
}: {
  caseLabel: string;
  subjectLabel: string;
  locationLabel: string;
  submitLabel: string;
}) => (
  <svg viewBox="0 0 280 200" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-[280px] h-auto rounded-2xl">
    <circle cx="220" cy="40" r="30" fill="#E3DE61" opacity=".35"/>
    <circle cx="40" cy="160" r="18" fill="#97B067" opacity=".25"/>
    <rect x="50" y="30" width="160" height="140" rx="12" fill="#fff" stroke="#2F5249" strokeWidth="2"/>
    <rect x="50" y="30" width="160" height="36" rx="12" fill="#2F5249"/>
    <rect x="50" y="54" width="160" height="12" fill="#2F5249"/>
    <text x="130" y="53" textAnchor="middle" fontSize="12" fontWeight="600" fill="#fff" fontFamily="Inter,sans-serif">{caseLabel}</text>
    <rect x="68" y="86" width="124" height="10" rx="3" fill="#E5EEE8"/>
    <text x="72" y="95" fontSize="8" fill="#437057" fontFamily="Inter,sans-serif" fontWeight="600">{subjectLabel}</text>
    <rect x="68" y="104" width="124" height="14" rx="4" fill="#F9FAFB" stroke="#E5E7EB" strokeWidth="1"/>
    <rect x="68" y="126" width="124" height="10" rx="3" fill="#E5EEE8"/>
    <text x="72" y="135" fontSize="8" fill="#437057" fontFamily="Inter,sans-serif" fontWeight="600">{locationLabel}</text>
    <rect x="68" y="144" width="124" height="14" rx="4" fill="#F9FAFB" stroke="#E5E7EB" strokeWidth="1"/>
    <rect x="88" y="166" width="84" height="26" rx="13" fill="#97B067"/>
    <text x="130" y="183" textAnchor="middle" fontSize="11" fontWeight="700" fill="#fff" fontFamily="Inter,sans-serif">{submitLabel}</text>
    <circle cx="244" cy="110" r="6" fill="#97B067" opacity=".6"/>
    <rect x="30" y="80" width="10" height="10" rx="2" transform="rotate(22 35 85)" fill="#E3DE61" opacity=".8"/>
  </svg>
);

const ParentStep2 = ({ matchesLabel }: { matchesLabel: string }) => (
  <svg viewBox="0 0 280 200" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-[280px] h-auto rounded-2xl">
    <path d="M240 30 l8 14 -16 0 z" fill="#E3DE61" opacity=".6"/>
    <circle cx="44" cy="48" r="14" fill="#97B067" opacity=".2"/>
    <g transform="translate(30,50)">
      <rect width="72" height="88" rx="8" fill="#fff" stroke="#2F5249" strokeWidth="1.5"/>
      <circle cx="36" cy="28" r="18" fill="#E5EEE8"/>
      <circle cx="36" cy="22" r="9" fill="#97B067"/>
      <path d="M18 46 q0-14 18-14 q18 0 18 14 z" fill="#2F5249"/>
      <rect x="10" y="62" width="52" height="5" rx="2" fill="#EEF0EC"/>
      <rect x="16" y="72" width="40" height="5" rx="2" fill="#EEF0EC"/>
      <circle cx="56" cy="12" r="10" fill="#fff" stroke="#97B067" strokeWidth="1.5"/>
      <polyline points="51,12 55,16 61,8" stroke="#059669" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    </g>
    <g transform="translate(118,50)">
      <rect width="72" height="88" rx="8" fill="#fff" stroke="#2F5249" strokeWidth="1.5"/>
      <circle cx="36" cy="28" r="18" fill="#FBF9E1"/>
      <circle cx="36" cy="22" r="9" fill="#E3DE61"/>
      <path d="M18 46 q0-14 18-14 q18 0 18 14 z" fill="#437057"/>
      <rect x="10" y="62" width="52" height="5" rx="2" fill="#EEF0EC"/>
      <rect x="16" y="72" width="40" height="5" rx="2" fill="#EEF0EC"/>
      <circle cx="56" cy="12" r="10" fill="#fff" stroke="#97B067" strokeWidth="1.5"/>
      <polyline points="51,12 55,16 61,8" stroke="#059669" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    </g>
    <g transform="translate(206,80)">
      <circle cx="22" cy="22" r="22" fill="#2F5249"/>
      <path d="M22 12 l2.5 7.5 7.5 2.5 -7.5 2.5 -2.5 7.5 -2.5 -7.5 -7.5 -2.5 7.5 -2.5 z" fill="#E3DE61"/>
    </g>
    <path d="M102 94 L116 94" stroke="#97B067" strokeWidth="2" strokeDasharray="3 3"/>
    <path d="M190 94 L204 94" stroke="#97B067" strokeWidth="2" strokeDasharray="3 3"/>
    <text x="140" y="162" textAnchor="middle" fontSize="11" fontWeight="700" fill="#2F5249" fontFamily="Inter,sans-serif">{matchesLabel}</text>
  </svg>
);

const ParentStep3 = ({
  nameLabel,
  profileLabel,
  ratingLabel,
  mathLabel,
  addMathLabel,
  chooseLabel,
}: {
  nameLabel: string;
  profileLabel: string;
  ratingLabel: string;
  mathLabel: string;
  addMathLabel: string;
  chooseLabel: string;
}) => (
  <svg viewBox="0 0 280 200" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-[280px] h-auto rounded-2xl">
    <circle cx="240" cy="160" r="22" fill="#E5EEE8"/>
    <rect x="30" y="30" width="200" height="140" rx="12" fill="#fff" stroke="#E5E7EB" strokeWidth="1.5"/>
    <rect x="30" y="30" width="200" height="40" rx="12" fill="#F9FAFB"/>
    <rect x="30" y="58" width="200" height="12" fill="#F9FAFB"/>
    <circle cx="58" cy="50" r="14" fill="#E5EEE8"/>
    <circle cx="58" cy="46" r="7" fill="#97B067"/>
    <path d="M46 62 q0-10 12-10 q12 0 12 10 z" fill="#2F5249"/>
    <text x="90" y="46" fontSize="13" fontWeight="700" fill="#111827" fontFamily="Inter,sans-serif">{nameLabel}</text>
    <text x="90" y="60" fontSize="10" fill="#6B7280" fontFamily="Inter,sans-serif">{profileLabel}</text>
    <text x="38" y="90" fontSize="13" fill="#E3A008" fontFamily="Inter,sans-serif">★★★★★</text>
    <text x="105" y="90" fontSize="10" fill="#6B7280" fontFamily="Inter,sans-serif">{ratingLabel}</text>
    <rect x="38" y="100" width="52" height="16" rx="8" fill="#F4F7EE"/>
    <text x="64" y="112" textAnchor="middle" fontSize="9" fontWeight="600" fill="#3F5F2C" fontFamily="Inter,sans-serif">{mathLabel}</text>
    <rect x="96" y="100" width="58" height="16" rx="8" fill="#F4F7EE"/>
    <text x="125" y="112" textAnchor="middle" fontSize="9" fontWeight="600" fill="#3F5F2C" fontFamily="Inter,sans-serif">{addMathLabel}</text>
    <rect x="38" y="138" width="80" height="26" rx="13" fill="#2F5249"/>
    <text x="78" y="155" textAnchor="middle" fontSize="11" fontWeight="700" fill="#fff" fontFamily="Inter,sans-serif">{chooseLabel}</text>
    <circle cx="210" cy="64" r="20" fill="#E3DE61"/>
    <polyline points="202,64 209,71 221,57" stroke="#2F5249" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ParentStep4 = ({
  bankTransferLabel,
  tngLabel,
}: {
  bankTransferLabel: string;
  tngLabel: string;
}) => (
  <svg viewBox="0 0 280 200" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-[280px] h-auto rounded-2xl">
    <rect x="30" y="30" width="180" height="130" rx="14" fill="#fff" stroke="#2F5249" strokeWidth="2"/>
    <rect x="30" y="52" width="180" height="30" fill="#2F5249"/>
    <rect x="52" y="100" width="28" height="22" rx="4" fill="#E3DE61"/>
    <circle cx="100" cy="111" r="4" fill="#EEF0EC"/>
    <circle cx="112" cy="111" r="4" fill="#EEF0EC"/>
    <circle cx="124" cy="111" r="4" fill="#EEF0EC"/>
    <circle cx="136" cy="111" r="4" fill="#EEF0EC"/>
    <rect x="52" y="135" width="60" height="18" rx="6" fill="#F4F7EE" stroke="#D3DFB7" strokeWidth="1"/>
    <text x="82" y="147" textAnchor="middle" fontSize="9" fontWeight="700" fill="#437057" fontFamily="Inter,sans-serif">{bankTransferLabel}</text>
    <rect x="120" y="135" width="52" height="18" rx="6" fill="#F4F7EE" stroke="#D3DFB7" strokeWidth="1"/>
    <text x="146" y="147" textAnchor="middle" fontSize="9" fontWeight="700" fill="#437057" fontFamily="Inter,sans-serif">{tngLabel}</text>
    <g transform="translate(200,30)">
      <circle cx="22" cy="22" r="22" fill="#E5EEE8"/>
      <rect x="13" y="19" width="18" height="14" rx="3" fill="#2F5249"/>
      <path d="M15 19 v-4 a7 7 0 0 1 14 0 v4" stroke="#2F5249" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
      <circle cx="22" cy="26" r="3" fill="#E3DE61"/>
    </g>
    <circle cx="40" cy="170" r="10" fill="#E3DE61" opacity=".5"/>
  </svg>
);

const ParentStep5 = () => (
  <svg viewBox="0 0 280 200" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-[280px] h-auto rounded-2xl">
    <path d="M50 50 q50-16 90 0 l0 100 q-40-16-90 0 z" fill="#fff" stroke="#2F5249" strokeWidth="2"/>
    <path d="M230 50 q-50-16-90 0 l0 100 q40-16 90 0 z" fill="#fff" stroke="#2F5249" strokeWidth="2"/>
    <line x1="140" y1="50" x2="140" y2="150" stroke="#2F5249" strokeWidth="2"/>
    <rect x="66" y="72" width="56" height="5" rx="2" fill="#97B067" opacity=".7"/>
    <rect x="66" y="84" width="64" height="5" rx="2" fill="#EEF0EC"/>
    <rect x="66" y="96" width="50" height="5" rx="2" fill="#EEF0EC"/>
    <rect x="66" y="108" width="60" height="5" rx="2" fill="#EEF0EC"/>
    <rect x="152" y="72" width="56" height="5" rx="2" fill="#97B067" opacity=".7"/>
    <rect x="152" y="84" width="64" height="5" rx="2" fill="#EEF0EC"/>
    <rect x="152" y="96" width="50" height="5" rx="2" fill="#EEF0EC"/>
    <rect x="152" y="108" width="60" height="5" rx="2" fill="#EEF0EC"/>
    <g transform="translate(106,24)">
      <circle cx="18" cy="18" r="18" fill="#2F5249"/>
      <path d="M18 8 l2 6 6 2 -6 2 -2 6 -2 -6 -6 -2 6 -2 z" fill="#E3DE61"/>
    </g>
    <rect x="56" y="30" width="8" height="8" rx="2" transform="rotate(20 60 34)" fill="#E3DE61" opacity=".8"/>
    <rect x="208" y="34" width="7" height="11" rx="2" transform="rotate(-24 212 40)" fill="#97B067" opacity=".8"/>
    <circle cx="236" cy="148" r="5" fill="#E3DE61" opacity=".6"/>
    <circle cx="48" cy="162" r="8" fill="#97B067" opacity=".25"/>
  </svg>
);

// --- Tutor SVG Illustrations ---
const TutorStep1 = ({
  mathLabel,
  physicsLabel,
  rateLabel,
}: {
  mathLabel: string;
  physicsLabel: string;
  rateLabel: string;
}) => (
  <svg viewBox="0 0 280 200" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-[280px] h-auto rounded-2xl">
    <circle cx="240" cy="40" r="26" fill="#E3DE61" opacity=".4"/>
    <rect x="50" y="24" width="160" height="152" rx="14" fill="#fff" stroke="#2F5249" strokeWidth="2"/>
    <circle cx="130" cy="68" r="28" fill="#E5EEE8"/>
    <circle cx="130" cy="60" r="14" fill="#97B067"/>
    <path d="M104 90 q0-20 26-20 q26 0 26 20 z" fill="#2F5249"/>
    <rect x="88" y="104" width="84" height="8" rx="4" fill="#2F5249" opacity=".8"/>
    <rect x="70" y="120" width="46" height="14" rx="7" fill="#F4F7EE" stroke="#D3DFB7" strokeWidth="1"/>
    <text x="93" y="131" textAnchor="middle" fontSize="8" fill="#3F5F2C" fontWeight="600" fontFamily="Inter,sans-serif">{mathLabel}</text>
    <rect x="122" y="120" width="52" height="14" rx="7" fill="#F4F7EE" stroke="#D3DFB7" strokeWidth="1"/>
    <text x="148" y="131" textAnchor="middle" fontSize="8" fill="#3F5F2C" fontWeight="600" fontFamily="Inter,sans-serif">{physicsLabel}</text>
    <text x="130" y="157" textAnchor="middle" fontSize="11" fontWeight="700" fill="#2F5249" fontFamily="Inter,sans-serif">{rateLabel}</text>
    <g transform="translate(178,32)">
      <circle cx="14" cy="14" r="14" fill="#E3DE61"/>
      <path d="M9 17 l6-6 4 4 -6 6 -4 0 0-4z" fill="#2F5249"/>
      <path d="M15 11 l4 4" stroke="#2F5249" strokeWidth="1.5" fill="none"/>
    </g>
    <circle cx="42" cy="165" r="8" fill="#97B067" opacity=".3"/>
  </svg>
);

const TutorStep2 = ({ identityDocumentLabel }: { identityDocumentLabel: string }) => (
  <svg viewBox="0 0 280 200" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-[280px] h-auto rounded-2xl">
    <path d="M240 40 l10 18 -20 0 z" fill="#97B067" opacity=".6"/>
    <rect x="50" y="40" width="155" height="100" rx="10" fill="#fff" stroke="#2F5249" strokeWidth="2"/>
    <rect x="50" y="40" width="155" height="30" rx="10" fill="#2F5249"/>
    <rect x="50" y="58" width="155" height="12" fill="#2F5249"/>
    <text x="128" y="58" textAnchor="middle" fontSize="10" fontWeight="700" fill="#fff" fontFamily="Inter,sans-serif">{identityDocumentLabel}</text>
    <rect x="64" y="80" width="40" height="46" rx="4" fill="#E5EEE8" stroke="#D1D5DB" strokeWidth="1"/>
    <circle cx="84" cy="94" r="10" fill="#97B067"/>
    <path d="M68 120 q0-12 16-12 q16 0 16 12 z" fill="#437057"/>
    <rect x="114" y="84" width="76" height="6" rx="3" fill="#EEF0EC"/>
    <rect x="114" y="96" width="60" height="6" rx="3" fill="#EEF0EC"/>
    <rect x="114" y="108" width="68" height="6" rx="3" fill="#EEF0EC"/>
    <g transform="translate(176,128)">
      <circle cx="22" cy="22" r="22" fill="#2F5249"/>
      <circle cx="22" cy="22" r="22" fill="none" stroke="#E3DE61" strokeWidth="2.5" strokeDasharray="3 5"/>
      <polyline points="13,22 20,30 33,14" fill="none" stroke="#E3DE61" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"/>
    </g>
    <circle cx="40" cy="60" r="10" fill="#E3DE61" opacity=".5"/>
  </svg>
);

const TutorStep3 = ({
  acceptLabel,
  passLabel,
}: {
  acceptLabel: string;
  passLabel: string;
}) => (
  <svg viewBox="0 0 280 200" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-[280px] h-auto rounded-2xl">
    <rect x="95" y="20" width="90" height="160" rx="16" fill="#2F5249"/>
    <rect x="103" y="32" width="74" height="136" rx="10" fill="#fff"/>
    <rect x="107" y="52" width="66" height="52" rx="8" fill="#F4F7EE" stroke="#D3DFB7" strokeWidth="1"/>
    <circle cx="120" cy="66" r="8" fill="#97B067"/>
    <rect x="132" y="60" width="34" height="5" rx="2" fill="#EEF0EC"/>
    <rect x="132" y="69" width="26" height="5" rx="2" fill="#EEF0EC"/>
    <rect x="110" y="84" width="30" height="12" rx="6" fill="#2F5249"/>
    <text x="125" y="94" textAnchor="middle" fontSize="8" fill="#fff" fontWeight="700" fontFamily="Inter,sans-serif">{acceptLabel}</text>
    <rect x="146" y="84" width="24" height="12" rx="6" fill="#F9FAFB" stroke="#E5E7EB" strokeWidth="1"/>
    <text x="158" y="94" textAnchor="middle" fontSize="8" fill="#6B7280" fontWeight="600" fontFamily="Inter,sans-serif">{passLabel}</text>
    <circle cx="185" cy="32" r="10" fill="#E3DE61"/>
    <text x="185" y="37" textAnchor="middle" fontSize="11" fontWeight="700" fill="#2F5249" fontFamily="Inter,sans-serif">1</text>
    <circle cx="42" cy="60" r="16" fill="#E3DE61" opacity=".3"/>
    <circle cx="246" cy="148" r="12" fill="#97B067" opacity=".25"/>
    <rect x="30" y="140" width="10" height="10" rx="2" transform="rotate(18 35 145)" fill="#E3DE61" opacity=".7"/>
  </svg>
);

const TutorStep4 = ({ equationLabel }: { equationLabel: string }) => (
  <svg viewBox="0 0 280 200" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-[280px] h-auto rounded-2xl">
    <rect x="40" y="30" width="164" height="110" rx="10" fill="#fff" stroke="#2F5249" strokeWidth="2"/>
    <rect x="40" y="30" width="164" height="14" rx="10" fill="#2F5249"/>
    <rect x="40" y="38" width="164" height="6" fill="#2F5249"/>
    <text x="122" y="64" textAnchor="middle" fontSize="13" fontWeight="700" fill="#2F5249" fontFamily="Inter,sans-serif">{equationLabel}</text>
    <line x1="58" y1="80" x2="186" y2="80" stroke="#EEF0EC" strokeWidth="1.5"/>
    <rect x="58" y="90" width="80" height="6" rx="3" fill="#97B067" opacity=".5"/>
    <rect x="58" y="102" width="64" height="6" rx="3" fill="#EEF0EC"/>
    <rect x="58" y="114" width="72" height="6" rx="3" fill="#EEF0EC"/>
    <circle cx="220" cy="70" r="16" fill="#E3DE61"/>
    <rect x="208" y="88" width="24" height="32" rx="6" fill="#437057"/>
    <line x1="208" y1="96" x2="192" y2="90" stroke="#E3DE61" strokeWidth="7" strokeLinecap="round"/>
    <circle cx="72" cy="160" r="10" fill="#97B067"/>
    <rect x="62" y="172" width="20" height="20" rx="5" fill="#2F5249"/>
    <circle cx="116" cy="160" r="10" fill="#E3DE61"/>
    <rect x="106" y="172" width="20" height="20" rx="5" fill="#437057"/>
    <circle cx="160" cy="160" r="10" fill="#E5EEE8"/>
    <rect x="150" y="172" width="20" height="20" rx="5" fill="#2F5249"/>
  </svg>
);

const TutorStep5 = ({
  monthlyPayoutLabel,
  netLabel,
}: {
  monthlyPayoutLabel: string;
  netLabel: string;
}) => (
  <svg viewBox="0 0 280 200" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-[280px] h-auto rounded-2xl">
    <rect x="40" y="50" width="180" height="110" rx="12" fill="#fff" stroke="#2F5249" strokeWidth="2"/>
    <rect x="40" y="50" width="180" height="32" rx="12" fill="#2F5249"/>
    <rect x="40" y="70" width="180" height="12" fill="#2F5249"/>
    <text x="130" y="70" textAnchor="middle" fontSize="11" fontWeight="700" fill="#fff" fontFamily="Inter,sans-serif">{monthlyPayoutLabel}</text>
    <text x="130" y="105" textAnchor="middle" fontSize="11" fontWeight="700" fill="#97B067" fontFamily="Inter,sans-serif">RM</text>
    <text x="130" y="130" textAnchor="middle" fontSize="30" fontWeight="800" fill="#2F5249" fontFamily="Inter,sans-serif">1,040</text>
    <text x="130" y="150" textAnchor="middle" fontSize="10" fill="#6B7280" fontFamily="Inter,sans-serif">{netLabel}</text>
    <path d="M218 105 q20 0 20 20 l0 30" stroke="#97B067" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeDasharray="4 4"/>
    <g transform="translate(222,148)">
      <rect width="32" height="32" rx="8" fill="#E5EEE8"/>
      <rect x="6" y="14" width="20" height="14" rx="2" fill="#2F5249"/>
      <rect x="10" y="10" width="12" height="6" rx="2" fill="#437057"/>
    </g>
    <rect x="44" y="38" width="8" height="8" rx="2" transform="rotate(20 48 42)" fill="#E3DE61" opacity=".9"/>
    <circle cx="230" cy="44" r="5" fill="#97B067" opacity=".6"/>
    <rect x="30" y="130" width="7" height="11" rx="2" transform="rotate(-22 34 136)" fill="#97B067" opacity=".8"/>
  </svg>
);

type LucideIcon = typeof Clock;

interface Step {
  number: string;
  tag: string;
  title: string;
  description: string;
  MetaIcon: LucideIcon;
  metaText: string;
  illustration: React.ReactNode;
  isLast: boolean;
}

const parentSteps: Step[] = [
  {
    number: '01',
    tag: 'Tell us',
    title: 'Post your case',
    description: 'Subject, level, location, and schedule. A short form — no sign-up required to start browsing.',
    MetaIcon: Clock,
    metaText: 'Takes about 2 minutes',
    illustration: null,
    isLast: false,
  },
  {
    number: '02',
    tag: 'We match',
    title: 'Get qualified matches',
    description: 'Our team shortlists 2–3 verified tutors who fit your subject, location, schedule, and budget. No cold calls, no guessing.',
    MetaIcon: Clock,
    metaText: 'Within 24 hours',
    illustration: null,
    isLast: false,
  },
  {
    number: '03',
    tag: 'You choose',
    title: 'Review & pick your tutor',
    description: 'Browse tutor profiles, qualifications, ratings, and experience. Choose the one that feels right — no pressure.',
    MetaIcon: Check,
    metaText: 'Your decision, always',
    illustration: null,
    isLast: false,
  },
  {
    number: '04',
    tag: 'Secure payment',
    title: 'Pay safely, 1 month upfront',
    description: "Your payment is held in secure escrow — it's only released to the tutor after lessons are delivered. Bank Transfer or TNG accepted.",
    MetaIcon: Lock,
    metaText: 'Funds held in escrow',
    illustration: null,
    isLast: false,
  },
  {
    number: '05',
    tag: 'Begin learning',
    title: 'Start your first lesson',
    description: 'Weekly lessons begin. Communicate directly with your tutor. Not satisfied? Switch tutors or get a refund for unused lessons.',
    MetaIcon: Check,
    metaText: 'Free to switch anytime',
    illustration: <ParentStep5 />,
    isLast: true,
  },
];

const tutorSteps: Step[] = [
  {
    number: '01',
    tag: 'Sign up',
    title: 'Create your profile',
    description: 'Add your subjects, teaching levels, location, and rates. Set your schedule and teaching mode (online, in-person, or both).',
    MetaIcon: Clock,
    metaText: 'Takes about 5 minutes',
    illustration: null,
    isLast: false,
  },
  {
    number: '02',
    tag: 'Verification',
    title: 'Get verified',
    description: 'Submit a support document or certificate for review. Add your IC to qualify for the Verified badge after approval.',
    MetaIcon: Clock,
    metaText: '2–3 business days',
    illustration: null,
    isLast: false,
  },
  {
    number: '03',
    tag: 'Matched',
    title: 'Receive a case',
    description: "When a parent posts a case matching your profile, you're notified by email and WhatsApp. Accept or decline — no obligation.",
    MetaIcon: Phone,
    metaText: 'Via email & WhatsApp',
    illustration: null,
    isLast: false,
  },
  {
    number: '04',
    tag: 'Teach',
    title: 'Conduct weekly lessons',
    description: 'You set the schedule and rate. Teach in-person or online — you and the parent coordinate directly. No platform middleman in your classroom.',
    MetaIcon: Monitor,
    metaText: 'Online or in-person',
    illustration: null,
    isLast: false,
  },
  {
    number: '05',
    tag: 'Get paid',
    title: 'Monthly payout to your bank',
    description: 'Automatic transfer every month-end. Months 1–2: you receive 75%. Month 3+: 100%. The commission resets per new case, not per tutor.',
    MetaIcon: Clock,
    metaText: 'Every month-end, automatic',
    illustration: null,
    isLast: true,
  },
];

const ProcessFlow: React.FC<ProcessFlowProps> = ({ audience }) => {
  const { t } = useI18n();

  const translatedParentSteps: Step[] = [
    {
      ...parentSteps[0],
      tag: t('processFlow.parent.step1.tag'),
      title: t('processFlow.parent.step1.title'),
      description: t('processFlow.parent.step1.description'),
      metaText: t('processFlow.parent.step1.meta'),
      illustration: (
        <ParentStep1
          caseLabel={t('processFlow.svg.parent.case')}
          subjectLabel={t('processFlow.svg.parent.subject')}
          locationLabel={t('processFlow.svg.parent.location')}
          submitLabel={t('processFlow.svg.parent.submit')}
        />
      ),
    },
    {
      ...parentSteps[1],
      tag: t('processFlow.parent.step2.tag'),
      title: t('processFlow.parent.step2.title'),
      description: t('processFlow.parent.step2.description'),
      metaText: t('processFlow.parent.step2.meta'),
      illustration: <ParentStep2 matchesLabel={t('processFlow.svg.parent.matches')} />,
    },
    {
      ...parentSteps[2],
      tag: t('processFlow.parent.step3.tag'),
      title: t('processFlow.parent.step3.title'),
      description: t('processFlow.parent.step3.description'),
      metaText: t('processFlow.parent.step3.meta'),
      illustration: (
        <ParentStep3
          nameLabel={t('processFlow.svg.parent.name')}
          profileLabel={t('processFlow.svg.parent.profile')}
          ratingLabel={t('processFlow.svg.parent.rating')}
          mathLabel={t('processFlow.svg.parent.subject.math')}
          addMathLabel={t('processFlow.svg.parent.subject.addMath')}
          chooseLabel={t('processFlow.svg.parent.choose')}
        />
      ),
    },
    {
      ...parentSteps[3],
      tag: t('processFlow.parent.step4.tag'),
      title: t('processFlow.parent.step4.title'),
      description: t('processFlow.parent.step4.description'),
      metaText: t('processFlow.parent.step4.meta'),
      illustration: (
        <ParentStep4
          bankTransferLabel={t('processFlow.svg.parent.bankTransfer')}
          tngLabel={t('processFlow.svg.parent.tng')}
        />
      ),
    },
    {
      ...parentSteps[4],
      tag: t('processFlow.parent.step5.tag'),
      title: t('processFlow.parent.step5.title'),
      description: t('processFlow.parent.step5.description'),
      metaText: t('processFlow.parent.step5.meta'),
      illustration: <ParentStep5 />,
    },
  ];

  const translatedTutorSteps: Step[] = [
    {
      ...tutorSteps[0],
      tag: t('processFlow.tutor.step1.tag'),
      title: t('processFlow.tutor.step1.title'),
      description: t('processFlow.tutor.step1.description'),
      metaText: t('processFlow.tutor.step1.meta'),
      illustration: (
        <TutorStep1
          mathLabel={t('processFlow.svg.tutor.subject.math')}
          physicsLabel={t('processFlow.svg.tutor.subject.physics')}
          rateLabel={t('processFlow.svg.tutor.rate')}
        />
      ),
    },
    {
      ...tutorSteps[1],
      tag: t('processFlow.tutor.step2.tag'),
      title: t('processFlow.tutor.step2.title'),
      description: t('processFlow.tutor.step2.description'),
      metaText: t('processFlow.tutor.step2.meta'),
      illustration: <TutorStep2 identityDocumentLabel={t('processFlow.svg.tutor.identityDocument')} />,
    },
    {
      ...tutorSteps[2],
      tag: t('processFlow.tutor.step3.tag'),
      title: t('processFlow.tutor.step3.title'),
      description: t('processFlow.tutor.step3.description'),
      metaText: t('processFlow.tutor.step3.meta'),
      illustration: (
        <TutorStep3
          acceptLabel={t('processFlow.svg.tutor.accept')}
          passLabel={t('processFlow.svg.tutor.pass')}
        />
      ),
    },
    {
      ...tutorSteps[3],
      tag: t('processFlow.tutor.step4.tag'),
      title: t('processFlow.tutor.step4.title'),
      description: t('processFlow.tutor.step4.description'),
      metaText: t('processFlow.tutor.step4.meta'),
      illustration: <TutorStep4 equationLabel={t('processFlow.svg.tutor.equation')} />,
    },
    {
      ...tutorSteps[4],
      tag: t('processFlow.tutor.step5.tag'),
      title: t('processFlow.tutor.step5.title'),
      description: t('processFlow.tutor.step5.description'),
      metaText: t('processFlow.tutor.step5.meta'),
      illustration: (
        <TutorStep5
          monthlyPayoutLabel={t('processFlow.svg.tutor.monthlyPayout')}
          netLabel={t('processFlow.svg.tutor.net')}
        />
      ),
    },
  ];
  const resolvedSteps = audience === 'parent' ? translatedParentSteps : translatedTutorSteps;

  return (
    <div>
      {resolvedSteps.map((step) => {
        const { MetaIcon } = step;
        return (
          <div
            key={step.number}
            className="grid grid-cols-[72px_1fr] md:grid-cols-[72px_1fr_1fr] gap-x-10 items-stretch"
          >
            {/* Number column */}
            <div className="flex flex-col items-center pt-1 relative">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-white text-lg relative z-10 ${
                  step.isLast
                    ? 'bg-accent shadow-[0_0_0_6px_rgba(151,176,103,0.2)]'
                    : 'bg-primary shadow-[0_0_0_6px_rgba(47,82,73,0.1)]'
                }`}
              >
                {step.number}
              </div>
              {!step.isLast && (
                <div className="flex-1 w-px bg-gradient-to-b from-primary/30 to-primary/5 mt-2" />
              )}
            </div>

            {/* Content */}
            <div className={!step.isLast ? 'pb-14' : ''}>
              <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[.14em] uppercase text-secondary mb-2.5">
                <span className="w-3 h-px bg-accent inline-block" />
                {step.tag}
              </div>
              <h3 className="text-2xl font-bold text-gray-900 tracking-tight leading-tight mb-2.5">
                {step.title}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-4 max-w-[400px]">
                {step.description}
              </p>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F4F7EE] border border-[#E4ECDC] text-xs text-secondary font-semibold">
                <MetaIcon className="w-3 h-3" />
                {step.metaText}
              </span>
            </div>

            {/* Illustration */}
            <div className={`hidden md:flex items-start justify-center ${!step.isLast ? 'pb-14' : ''}`}>
              {step.illustration}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProcessFlow;
